<template>
  <div>
    <el-dialog
      :visible.sync="changePassModal"
      @close="$refs.security.resetFields()"
      width="500px"
      :fullscreen="$mq === 'xs'"
    >
      <div slot="title">
        <h4 class="title title-up">{{ $t('Change password')}}</h4>
      </div>
      <el-form ref="security" :model="security" hide-required-asterisk>
        <el-form-item prop="currentPassword" :label="$t('Current password')">
          <el-input
            type="password"
            v-model="security.currentPassword"
            data-vv-name="currentPassword"
          ></el-input>
        </el-form-item>
        <el-form-item prop="newPassword" :label="$t('New password')">
          <el-input
            type="password"
            autoComplete="current-password"
            v-model="security.newPassword"
            data-vv-name="newPassword"
            ref="newPassword"
          ></el-input>
        </el-form-item>
        <el-form-item prop="confirmedPassword" :label="$t('New password again')" :error="getError('confirmedPassword')">
          <el-input
            type="password"
            autoComplete="new-password"
            v-model="security.confirmedPassword"
            v-validate="`${security.newPassword ? 'required|' : ''}|confirmed:newPassword`"
            data-vv-name="confirmedPassword"
          ></el-input>
        </el-form-item>
        <el-form-item style="text-align: right; margin-bottom: 0">
          <el-button @click.native="changePassModal = false">{{ $t('Cancel') }}</el-button>
          <el-button @click="changePassword" :disabled="!isValidPasswordForm" type="primary">{{ $t('Change password') }}</el-button>
        </el-form-item>
      </el-form>
    </el-dialog>
    <card>
      <h4 class="card-title">{{ $t('Contact details') }}</h4>
      <div slot="default">
        <el-form ref="personalInfo" :model="credentials" hide-required-asterisk label-position="left" class="personal-info">
          <el-checkbox v-if="isIndividual" style="margin-bottom: 15px" v-model="credentials.soleProprietor">
            {{ $t('Sole proprietor') }}
          </el-checkbox>
          <el-form-item :label="$t('First name')" v-if="isIndividual" :error="getError('firstname')">
            <el-input
              v-model="credentials.firstname"
              data-vv-name="firstname"
              key="firstname"
              v-validate="'required'"></el-input>
          </el-form-item>
          <el-form-item :label="$t('Last name')" v-if="isIndividual" :error="getError('lastname')">
            <el-input
              v-model="credentials.lastname"
              data-vv-name="lastname"
              key="lastname"
              v-validate="'required'">
            </el-input>
          </el-form-item>
          <el-form-item :label="$t('Middle name')" v-if="isIndividual">
            <el-input
              v-model="credentials.middlename"
              data-vv-name="middlename"
              key="middlename">
            </el-input>
          </el-form-item>
          <el-form-item :label="$t('Company name')" v-if="!isIndividual" :error="getError('organization')">
            <el-input
              v-model="credentials.organization"
              data-vv-name="organization"
              key="organization"
              v-validate="'required'">
            </el-input>
          </el-form-item>
          <el-form-item :label="$t('Email')" :error="getError('email')">
            <el-input type="email" v-model="credentials.email" data-vv-name="email"></el-input>
          </el-form-item>
          <el-form-item :label="$t('Phone')">
            <el-input type="tel" v-model="credentials.mobile" id="mobile"></el-input>
          </el-form-item>
          <el-form-item :label="$t('Contact person')" v-if="isLegal || isSoleProprietor">
            <el-input v-model="credentials.kont_person" id="kont_person"></el-input>
          </el-form-item>
          <el-form-item :label="$t('Password')">
            <el-button @click="changePassModal = true" :disabled="changePassModal" size="mini">{{ $t('Change password') }}</el-button>
          </el-form-item>
          <el-button type="primary" size="medium" :loading="loading" @click="updateCredentials">{{ $t('Save') }}</el-button>
        </el-form>
      </div>
    </card>
    <card v-if="!isIndividual || isSoleProprietor">
      <h4 class="card-title">{{ $t(isSoleProprietor ? 'Bank details' : 'Company details') }}</h4>
      <div slot="default">
        <el-form ref="companyDetails" :model="credentials" hide-required-asterisk>
          <div class="row">
            <div class="col-md-6">
              <el-form-item :label="$t('INN')" :error="getError('inn')">
                <el-input
                  v-on:keypress="isNumber(event)"
                  v-model="credentials.inn"
                  data-vv-name="inn"
                  v-validate="isLegal ? 'digits:10' : 'digits:12'"
                ></el-input>
              </el-form-item>
              <el-form-item v-if="isLegal" :label="$t('KPP')" :error="getError('kpp')">
                <el-input
                  v-model="credentials.kpp"
                  data-vv-name="kpp"
                  v-validate="'digits:9'"
                ></el-input>
              </el-form-item>
              <el-form-item :label="$t(isIndividual ? 'OGRNIP' : 'OGRN')" :error="getError(isIndividual ? 'ogrnip' : 'ogrn')">
                <el-input
                  v-model="credentials.ogrn"
                  :data-vv-name="isIndividual ? 'ogrnip' : 'ogrn'"
                  v-validate="isIndividual ? 'digits:15' : 'digits:13'">
                </el-input>
              </el-form-item>
              <el-form-item :label="$t('OKPO')" :error="getError('okpo')">
                <el-input
                  v-model="credentials.okpo"
                  data-vv-name="okpo"
                  v-validate="'numeric'"
                ></el-input>
              </el-form-item>
              <el-form-item v-if="isSoleProprietor" :label="$t('BIK')" :error="getError('bik')">
                <el-input
                  v-model="credentials.bik"
                  data-vv-name="bik"
                  v-validate="'digits:9'"
                ></el-input>
              </el-form-item>
            </div>
            <div class="col-md-6">
              <el-form-item :label="$t('Full bank name')" :error="getError('bankName')">
                <el-input
                  v-model="credentials.bankName"
                  data-vv-name="bankName"
                ></el-input>
              </el-form-item>
              <el-form-item :label="$t('Checking account')" :error="getError('settl')">
                <el-input
                  v-model="credentials.settl"
                  data-vv-name="settl"
                  v-validate="'digits:20'">
                </el-input>
              </el-form-item>
              <el-form-item :label="$t('Correspondent account')" :error="getError('corr')">
                <el-input
                  v-model="credentials.corr"
                  data-vv-name="corr"
                  v-validate="'digits:20'"
                ></el-input>
              </el-form-item>
              <el-form-item v-if="!isSoleProprietor" :label="$t('BIK')" :error="getError('bik')">
                <el-input
                  v-model="credentials.bik"
                  data-vv-name="bik"
                  v-validate="'digits:9'"
                ></el-input>
              </el-form-item>
            </div>
          </div>
          <el-button size="medium" type="primary" @click="updateCompanyDetails">{{ $t('Save') }}</el-button>
        </el-form>
      </div>
    </card>
  </div>
</template>

<script>
import { Form, FormItem, Input, Button, Checkbox } from 'element-ui'
import { Modal } from '@/ui'
import { mapState } from 'vuex'
import Inputmask from 'inputmask'

export default {
  name: 'PersonalInfo',
  components: {
    'el-form': Form,
    'el-form-item': FormItem,
    'el-input': Input,
    'el-button': Button,
    'el-checkbox': Checkbox,
    Modal
  },
  props: {
    credentials: {
      type: Object,
      required: true
    },
    loading: {
      type: Boolean
    }
  },
  data() {
    return {
      changePassModal: false,
      security: {
        currentPassword: '',
        newPassword: '',
        confirmedPassword: ''
      }
    }
  },
  computed: {
    ...mapState({
      phoneMask: state => state.config.data.phoneMask
    }),
    isIndividual: function() {
      return this.credentials.type === 2
    },
    isLegal: function() {
      return this.credentials.type === 1
    },
    isSoleProprietor: function() {
      return this.credentials.soleProprietor
    },
    isValidPasswordForm: function() {
      return (this.security.currentPassword && this.security.newPassword && this.security.confirmedPassword && this.security.confirmedPassword === this.security.newPassword) || false
    }
  },
  methods: {
    updateCredentials: function() {
      this.$validator.validateAll().then(result => {
        if (result) {
          this.$emit('updateCredentials')
        }
      })
    },
    updateCompanyDetails: function() {
      this.$validator.validateAll().then(result => {
        if (result) {
          this.$emit('updateCredentials')
        }
      })
    },
    changePassword: function() {
      this.$validator.validateAll().then(result => {
        if (result) {
          this.$emit('changePassword', this.security)
          this.$refs.security.resetFields()
        }
      })
    },
    getError (fieldName) {
      let error = this.errors.first(fieldName)
      if (error && error[error.length - 1] === '.') {
        error = error.slice(0, -1)
      }

      return this.$t(error)
    }
  },
  mounted() {
    var im = new Inputmask(this.phoneMask)
    im.mask(document.getElementById('mobile'))
  }
}
</script>

<style lang="scss" scoped>
  .card-title {
    margin-bottom: 30px !important;
  }

</style>
